import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  getSettings,
  updateProfile,
  updateScheduling,
  getEmailConfig,
  updateEmailConfig,
  testEmail,
  getBilling,
  getBillingPortal,
} from "../lib/api.ts";
import { useTheme, ACCENT_OPTIONS, type Accent } from "../lib/theme.tsx";
import { useCalendarPrefs } from "../lib/calendar-prefs.ts";
import { SUPPORTED_LOCALES } from "../lib/i18n.ts";
import { MessagingTab } from "../components/MessagingTab.tsx";
import IntegrationsTab from "./settings/IntegrationsTab";
import { PaymentsTab } from "./settings/PaymentsTab";

function MessagingTabWrapper({ slug }: { slug: string }) {
  return <MessagingTab slug={slug} />;
}

type Tab = "profile" | "brand" | "booking_page" | "scheduling" | "messaging" | "billing" | "integrations" | "payments";

// ── Profile Tab ───────────────────────────────────────────────────────────────

interface ProfileData {
  name: string;
  email: string;
  phone: string;
  address: string;
  website: string;
  timezone: string;
}

function ProfileTab({ slug }: { slug: string }) {
  const [form, setForm] = useState<ProfileData>({
    name: "", email: "", phone: "", address: "", website: "", timezone: "UTC",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getSettings(slug)
      .then((data: { profile?: Record<string, unknown> }) => {
        if (data.profile) {
          const p = data.profile;
          setForm({
            name:     (p.name     as string) ?? "",
            email:    (p.email    as string) ?? "",
            phone:    (p.phone    as string) ?? "",
            address:  (p.address_line1 as string) ?? "",
            website:  (p.website_url  as string) ?? "",
            timezone: (p.timezone as string) ?? "UTC",
          });
        }
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  const set = <K extends keyof ProfileData>(k: K, v: ProfileData[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await Promise.all([
        updateProfile(slug, {
          name:          form.name,
          email:         form.email,
          phone:         form.phone,
          address_line1: form.address,
          website_url:   form.website,
        }),
        updateScheduling(slug, { timezone: form.timezone }),
      ]);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: "32px 0", color: "var(--ink-faint)", fontSize: 14 }}>Loading…</div>;

  return (
    <form onSubmit={submit} className="space-y-10 max-w-2xl">
      {error && <div className="alert-error">{error}</div>}
      {success && <div className="alert-success">Profile saved.</div>}

      <SettingsSection
        title="Identity"
        lead="Public details — these show up on your booking page, in confirmation emails, and on the customer-facing pages."
      >
        <FieldRow label="Business name">
          <input className="input" value={form.name} onChange={(e) => set("name", e.target.value)} />
        </FieldRow>
        <FieldRow label="Contact email">
          <input type="email" className="input" value={form.email} onChange={(e) => set("email", e.target.value)} />
        </FieldRow>
        <FieldRow label="Phone">
          <input className="input" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </FieldRow>
        <FieldRow label="Website">
          <input type="url" className="input" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://example.com" />
        </FieldRow>
      </SettingsSection>

      <SettingsSection
        title="Location"
        lead="Optional — only shown on the booking page if you set it. Skip if you're appointment-only without a physical location."
      >
        <FieldRow label="Address">
          <input className="input" value={form.address} onChange={(e) => set("address", e.target.value)} />
        </FieldRow>
        <FieldRow label="Timezone" hint="All booking times are stored in UTC and converted to this zone when displayed.">
          <select className="input" value={form.timezone} onChange={(e) => set("timezone", e.target.value)}>
            <option value="UTC">UTC</option>
            <option value="America/New_York">America/New_York</option>
            <option value="America/Chicago">America/Chicago</option>
            <option value="America/Denver">America/Denver</option>
            <option value="America/Los_Angeles">America/Los_Angeles</option>
            <option value="Europe/London">Europe/London</option>
            <option value="Europe/Paris">Europe/Paris</option>
            <option value="Asia/Kolkata">Asia/Kolkata</option>
            <option value="Asia/Tokyo">Asia/Tokyo</option>
            <option value="Australia/Sydney">Australia/Sydney</option>
          </select>
        </FieldRow>
      </SettingsSection>

      <div>
        <button type="submit" disabled={saving} className="btn-primary">
          {saving ? "Saving…" : "Save profile"}
        </button>
      </div>
    </form>
  );
}

// ── Reusable building blocks ──────────────────────────────────────────────────

function SettingsSection({
  title,
  lead,
  children,
}: {
  title: string;
  lead?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2
        style={{
          fontFamily: "'Source Serif 4', Georgia, serif",
          fontWeight: 500,
          fontSize: 22,
          letterSpacing: "-0.012em",
          color: "var(--ink)",
          marginBottom: 6,
        }}
      >
        {title}
      </h2>
      {lead && (
        <p style={{ fontSize: 14, color: "var(--ink-soft)", marginBottom: 18, maxWidth: "60ch" }}>{lead}</p>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>
    </section>
  );
}

function FieldRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
      {hint && (
        <div style={{ fontSize: 12, color: "var(--ink-faint)", marginTop: 6 }}>{hint}</div>
      )}
    </div>
  );
}

// ── Scheduling Tab ────────────────────────────────────────────────────────────

interface SchedulingData {
  slot_duration_min: number;
  buffer_after_min: number;
  advance_booking_days: number;
  min_notice_hours: number;
  allow_same_day: boolean;
}

function SchedulingTab({ slug }: { slug: string }) {
  const [form, setForm] = useState<SchedulingData>({
    slot_duration_min: 60,
    buffer_after_min: 0,
    advance_booking_days: 60,
    min_notice_hours: 1,
    allow_same_day: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getSettings(slug)
      .then((data: { scheduling?: SchedulingData }) => {
        if (data.scheduling) setForm(data.scheduling);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  const set = <K extends keyof SchedulingData>(k: K, v: SchedulingData[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateScheduling(slug, form);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: "32px 0", color: "var(--ink-faint)", fontSize: 14 }}>Loading…</div>;

  return (
    <form onSubmit={submit} className="space-y-10 max-w-2xl">
      {error && <div className="alert-error">{error}</div>}
      {success && <div className="alert-success">Scheduling settings saved.</div>}

      <SettingsSection
        title="Slots"
        lead="How long each booking takes by default and how much breathing room to leave between them. Service-specific durations override these."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldRow label="Default slot length (min)">
            <input type="number" min={5} className="input" value={form.slot_duration_min}
              onChange={(e) => set("slot_duration_min", Number(e.target.value))} />
          </FieldRow>
          <FieldRow label="Buffer after appointment (min)" hint="Time blocked off after each booking — useful for cleanup or notes.">
            <input type="number" min={0} className="input" value={form.buffer_after_min}
              onChange={(e) => set("buffer_after_min", Number(e.target.value))} />
          </FieldRow>
        </div>
      </SettingsSection>

      <SettingsSection
        title="Booking window"
        lead="How far out customers can book and how soon before an appointment they can still grab a slot."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldRow label="Advance booking (days)">
            <input type="number" min={1} className="input" value={form.advance_booking_days}
              onChange={(e) => set("advance_booking_days", Number(e.target.value))} />
          </FieldRow>
          <FieldRow label="Minimum notice (hours)" hint="Bookings closer than this to start time are blocked.">
            <input type="number" min={0} className="input" value={form.min_notice_hours}
              onChange={(e) => set("min_notice_hours", Number(e.target.value))} />
          </FieldRow>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
          <input
            id="same_day"
            type="checkbox"
            checked={form.allow_same_day}
            onChange={(e) => set("allow_same_day", e.target.checked)}
            style={{ width: 16, height: 16, accentColor: "var(--ink)" }}
          />
          <label htmlFor="same_day" style={{ fontSize: 14, color: "var(--ink)" }}>
            Allow same-day bookings
          </label>
        </div>
      </SettingsSection>

      <div>
        <button type="submit" disabled={saving} className="btn-primary">
          {saving ? "Saving…" : "Save scheduling"}
        </button>
      </div>

      <CalendarDisplaySection slug={slug} />
    </form>
  );
}

// ── Calendar display (locale, 12/24h, week-start) ────────────────────────────
//
// These prefs are stored per-tenant in localStorage via useCalendarPrefs —
// no API roundtrip. Saves happen on every change (no submit button).

function CalendarDisplaySection({ slug }: { slug: string }) {
  const [prefs, update] = useCalendarPrefs(slug);
  return (
    <SettingsSection
      title="Calendar display"
      lead="How dates and times appear in the dashboard. Saved to your browser only — switching browsers resets them."
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldRow label="Language / region" hint="Affects date format, weekday names, and currency.">
          <select
            className="input"
            value={prefs.locale ?? ""}
            onChange={(e) => update({ locale: e.target.value || null })}
          >
            <option value="">Follow browser</option>
            {SUPPORTED_LOCALES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </FieldRow>
        <FieldRow label="Time format">
          <select
            className="input"
            value={prefs.hourFormat}
            onChange={(e) => update({ hourFormat: e.target.value as "12" | "24" })}
          >
            <option value="12">12-hour (3:00 PM)</option>
            <option value="24">24-hour (15:00)</option>
          </select>
        </FieldRow>
        <FieldRow label="Week starts on">
          <select
            className="input"
            value={String(prefs.weekStart)}
            onChange={(e) => update({ weekStart: Number(e.target.value) as 0 | 1 | 6 })}
          >
            <option value="0">Sunday</option>
            <option value="1">Monday</option>
            <option value="6">Saturday</option>
          </select>
        </FieldRow>
      </div>
    </SettingsSection>
  );
}

// ── Email Tab ─────────────────────────────────────────────────────────────────

interface EmailConfig {
  from_name: string;
  from_email: string;
  send_confirmation: boolean;
  send_reminder: boolean;
  reminder_hours_before: number;
  send_cancellation: boolean;
}

// Legacy EmailTab kept for reference; the live "Messaging" tab uses
// MessagingTab which supports multiple providers per channel. Marked
// unused — TS6133 disabled via _ prefix to keep it discoverable in git
// blame.
// @ts-expect-error retained for reference; will delete once Messaging is bedded in.
function _EmailTab({ slug }: { slug: string }) {
  const [form, setForm] = useState<EmailConfig>({
    from_name: "",
    from_email: "",
    send_confirmation: true,
    send_reminder: true,
    reminder_hours_before: 24,
    send_cancellation: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail_, setTestEmail_] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    getEmailConfig(slug)
      .then((data: EmailConfig) => setForm(data))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  const set = <K extends keyof EmailConfig>(k: K, v: EmailConfig[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await updateEmailConfig(slug, form);
      setSuccess("Email settings saved!");
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail_) return;
    setTesting(true);
    setError(null);
    setSuccess(null);
    try {
      await testEmail(slug, { to: testEmail_ });
      setSuccess("Test email sent!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div style={{ padding: "32px 0", color: "var(--ink-faint)", fontSize: 14 }}>Loading…</div>;

  return (
    <div className="space-y-10 max-w-2xl">
      <form onSubmit={submit} className="space-y-10">
        {error && <div className="alert-error">{error}</div>}
        {success && <div className="alert-success">{success}</div>}

        <SettingsSection
          title="Sender"
          lead="What customers see in their inbox when Zedule emails them on your behalf."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldRow label="From name">
              <input className="input" value={form.from_name} onChange={(e) => set("from_name", e.target.value)} placeholder="ACME Clinic" />
            </FieldRow>
            <FieldRow label="From email">
              <input type="email" className="input" value={form.from_email} onChange={(e) => set("from_email", e.target.value)} placeholder="hello@acme.example" />
            </FieldRow>
          </div>
        </SettingsSection>

        <SettingsSection
          title="What to send"
          lead="Toggle the automatic emails we send for each appointment lifecycle event."
        >
          <Toggle id="conf" label="Booking confirmation" checked={form.send_confirmation}
            onChange={(v) => set("send_confirmation", v)} />
          <Toggle id="canc" label="Cancellation notice" checked={form.send_cancellation}
            onChange={(v) => set("send_cancellation", v)} />
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <input
              id="remind"
              type="checkbox"
              checked={form.send_reminder}
              onChange={(e) => set("send_reminder", e.target.checked)}
              style={{ width: 16, height: 16, accentColor: "var(--ink)" }}
            />
            <label htmlFor="remind" style={{ fontSize: 14, color: "var(--ink)" }}>Reminder</label>
            {form.send_reminder && (
              <>
                <input
                  type="number" min={1} max={168}
                  className="input"
                  style={{ width: 80, padding: "6px 10px", fontSize: 13 }}
                  value={form.reminder_hours_before}
                  onChange={(e) => set("reminder_hours_before", Number(e.target.value))}
                />
                <span style={{ fontSize: 13, color: "var(--ink-soft)" }}>hours before the appointment</span>
              </>
            )}
          </div>
        </SettingsSection>

        <div>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? "Saving…" : "Save email settings"}
          </button>
        </div>
      </form>

      <SettingsSection
        title="Send a test"
        lead="Sends a sample confirmation email to the address below using the sender details above. Use to verify formatting and deliverability."
      >
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="email"
            placeholder="test@example.com"
            className="input"
            style={{ flex: 1 }}
            value={testEmail_}
            onChange={(e) => setTestEmail_(e.target.value)}
          />
          <button
            onClick={sendTest}
            disabled={testing || !testEmail_}
            className="btn-secondary"
          >
            {testing ? "Sending…" : "Send test"}
          </button>
        </div>
      </SettingsSection>
    </div>
  );
}

function Toggle({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 16, height: 16, accentColor: "var(--ink)" }}
      />
      <label htmlFor={id} style={{ fontSize: 14, color: "var(--ink)" }}>{label}</label>
    </div>
  );
}

// ── Brand Tab ─────────────────────────────────────────────────────────────────
// Controls the dashboard's accent template. localStorage paints instantly on
// boot, then `business_profile.accent` (per-tenant) overrides it on Layout
// entry. Saving here PATCHes /settings/profile so other devices pick up the
// change. The 4 templates are visual presets, not arbitrary colors — keeps the
// system disciplined and prevents UI drift.

function BrandTab({ slug }: { slug: string }) {
  const { theme: mode, accent, setAccent, toggle } = useTheme();
  const [saving, setSaving] = useState<Accent | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleAccent = async (next: Accent) => {
    if (next === accent) return;
    setAccent(next); // instant local + cache
    setSaving(next);
    setSaveError(null);
    try {
      await updateProfile(slug, { accent: next });
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-10 max-w-2xl">
      <section>
        <h2
          style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontWeight: 500,
            fontSize: 22,
            letterSpacing: "-0.012em",
            color: "var(--ink)",
            marginBottom: 6,
          }}
        >
          Accent
        </h2>
        <p style={{ fontSize: 14, color: "var(--ink-soft)", marginBottom: 18, maxWidth: "60ch" }}>
          One color, used sparingly across the dashboard — the live indicator, the upcoming KPI, the
          italic emphasis word in the headline, and small avatar tints. Pick the one that suits your
          business; the rest of the design stays the same.
        </p>

        {saveError && (
          <div className="alert-error" style={{ marginBottom: 12 }}>{saveError}</div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ACCENT_OPTIONS.map((opt) => {
            const selected = accent === opt.id;
            const isSaving = saving === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => handleAccent(opt.id as Accent)}
                aria-pressed={selected}
                disabled={isSaving}
                className="text-left"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: 16,
                  borderRadius: 12,
                  border: "none",
                  cursor: isSaving ? "wait" : "pointer",
                  background: "var(--paper-raised)",
                  boxShadow: selected
                    ? "0 0 0 2px var(--ink), 0 4px 14px oklch(0.20 0.025 280 / 0.08), 0 0 0 1px var(--rule)"
                    : "var(--shadow-sm)",
                  transition: "transform 120ms ease, box-shadow 120ms ease",
                  opacity: isSaving ? 0.7 : 1,
                }}
              >
                <span
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 999,
                    background: swatchColor(opt.id as Accent),
                    boxShadow: "0 0 0 1px var(--rule)",
                    flexShrink: 0,
                  }}
                  aria-hidden="true"
                />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: "block", fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>
                    {opt.label}
                  </span>
                  <span style={{ display: "block", fontSize: 12.5, color: "var(--ink-soft)", marginTop: 2 }}>
                    {opt.hint}
                  </span>
                </span>
                {isSaving && (
                  <span style={{ fontSize: 11, color: "var(--ink-faint)", fontStyle: "italic" }}>saving…</span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <h2
          style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontWeight: 500,
            fontSize: 22,
            letterSpacing: "-0.012em",
            color: "var(--ink)",
            marginBottom: 6,
          }}
        >
          Mode
        </h2>
        <p style={{ fontSize: 14, color: "var(--ink-soft)", marginBottom: 18, maxWidth: "60ch" }}>
          Light is best for daytime reception use. Dark works well in low-light salons or on-call
          screens. The accent stays the same across both.
        </p>

        <div className="grid grid-cols-2 gap-3" style={{ maxWidth: 360 }}>
          {(["light", "dark"] as const).map((m) => {
            const selected = mode === m;
            return (
              <button
                key={m}
                type="button"
                onClick={() => { if (mode !== m) toggle(); }}
                aria-pressed={selected}
                style={{
                  padding: "14px 18px",
                  borderRadius: 12,
                  border: "none",
                  cursor: "pointer",
                  background: "var(--paper-raised)",
                  boxShadow: selected
                    ? "0 0 0 2px var(--ink), 0 4px 14px oklch(0.20 0.025 280 / 0.08), 0 0 0 1px var(--rule)"
                    : "var(--shadow-sm)",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "var(--ink)",
                  transition: "transform 120ms ease, box-shadow 120ms ease",
                  textTransform: "capitalize",
                }}
              >
                {m}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function swatchColor(accent: Accent): string {
  switch (accent) {
    case "indigo": return "oklch(0.55 0.18 280)";
    case "forest": return "oklch(0.50 0.12 165)";
    case "rose":   return "oklch(0.62 0.18 15)";
    case "amber":  return "oklch(0.68 0.16 60)";
  }
}

// ── Booking Page Tab ──────────────────────────────────────────────────────────

const TEMPLATES = [
  { id: "classic",        label: "Classic",        desc: "Service → Staff → Date/Time" },
  { id: "provider_first", label: "Provider-First",  desc: "Pick provider → Services → Schedule" },
  { id: "date_first",     label: "Date-First",      desc: "Pick date → See available" },
  { id: "simple",         label: "Simple",          desc: "Just pick a time" },
  { id: "catalog",        label: "Catalog",         desc: "Browse & build a package" },
] as const;

type TemplateId = typeof TEMPLATES[number]["id"];

interface BrandingData {
  booking_template: TemplateId;
  primary_color: string;
  font_family: string;
  corner_radius: string;
  logo_url: string;
  cover_url: string;
}

function BookingPageTab({ slug }: { slug: string }) {
  const [form, setForm] = useState<BrandingData>({
    booking_template: "classic",
    primary_color: "#6366f1",
    font_family: "Inter",
    corner_radius: "12px",
    logo_url: "",
    cover_url: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    getSettings(slug)
      .then((data: { branding?: BrandingData }) => {
        if (data.branding) setForm((f) => ({ ...f, ...data.branding }));
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  const set = <K extends keyof BrandingData>(k: K, v: BrandingData[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateProfile(slug, form);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const openPreview = () => {
    window.open(`https://book.zedule.app/${slug}`, "_blank");
  };

  // ── Share + embed: copy-to-clipboard with brief confirmation ──────────────
  // `copied` carries the key of the most-recently-copied snippet so we can
  // toggle the button label inline. `null` means nothing is in the "just
  // copied" window. The window auto-closes after 1.6s.
  const [copied, setCopied] = useState<"link" | "embed" | null>(null);
  const bookingUrl = `https://book.zedule.app/${slug}`;
  const embedSnippet = `<iframe\n  src="${bookingUrl}?embed=1"\n  width="100%" height="820"\n  style="border:none;border-radius:12px;max-width:720px;"\n  loading="lazy"\n  title="Book an appointment"\n></iframe>`;

  const copy = async (key: "link" | "embed", text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((cur) => (cur === key ? null : cur)), 1600);
    } catch {
      // Fallback: prompt the user to copy manually
      window.prompt("Copy this:", text);
    }
  };

  if (loading) return <div className="text-sm" style={{ color: "var(--text-muted)" }}>Loading...</div>;

  return (
    <form onSubmit={submit} className="space-y-8 max-w-2xl">
      {error && <div className="alert-error">{error}</div>}
      {success && <div className="alert-success">Booking page settings saved!</div>}

      {/* Template Selector */}
      <section>
        <h2 className="text-base font-semibold mb-1" style={{ color: "var(--text)" }}>Booking Template</h2>
        <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>Choose how customers flow through your booking page.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TEMPLATES.map((tmpl) => {
            const selected = form.booking_template === tmpl.id;
            return (
              <label
                key={tmpl.id}
                className="flex items-start gap-3 cursor-pointer rounded-lg p-4 transition-colors"
                style={{
                  border: `2px solid ${selected ? "var(--accent)" : "var(--card-border)"}`,
                  background: selected ? "var(--accent-light)" : "var(--card-bg)",
                }}
              >
                <input
                  type="radio"
                  name="booking_template"
                  value={tmpl.id}
                  checked={selected}
                  onChange={() => set("booking_template", tmpl.id)}
                  className="mt-0.5 h-4 w-4"
                />
                <div>
                  <div className="font-medium text-sm" style={{ color: selected ? "var(--accent-text)" : "var(--text)" }}>
                    {tmpl.label}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{tmpl.desc}</div>
                </div>
              </label>
            );
          })}
        </div>
      </section>

      {/* Branding */}
      <section className="space-y-4">
        <h2 className="text-base font-semibold" style={{ color: "var(--text)" }}>Branding</h2>

        <div className="grid grid-cols-2 gap-4">
          {/* Primary Color */}
          <div>
            <label className="label">Primary Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.primary_color}
                onChange={(e) => set("primary_color", e.target.value)}
                className="h-9 w-14 cursor-pointer rounded p-0.5"
                style={{ border: "1px solid var(--card-border)" }}
              />
              <span className="text-sm font-mono" style={{ color: "var(--text-muted)" }}>{form.primary_color}</span>
            </div>
          </div>

          {/* Font Family */}
          <div>
            <label className="label">Font</label>
            <select
              className="input"
              value={form.font_family}
              onChange={(e) => set("font_family", e.target.value)}
            >
              <option value="Inter">Inter</option>
              <option value="Poppins">Poppins</option>
              <option value="Playfair Display">Playfair Display</option>
            </select>
          </div>
        </div>

        {/* Corner Radius */}
        <div>
          <label className="label">Corner Radius</label>
          <div className="flex gap-3 mt-1">
            {[
              { value: "4px",  label: "Sharp" },
              { value: "12px", label: "Rounded" },
              { value: "20px", label: "Pill" },
            ].map((opt) => {
              const active = form.corner_radius === opt.value;
              return (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 cursor-pointer rounded-md px-3 py-2 text-sm transition-colors"
                  style={{
                    border: `1px solid ${active ? "var(--accent)" : "var(--card-border)"}`,
                    color: active ? "var(--accent-text)" : "var(--text-muted)",
                    background: active ? "var(--accent-light)" : "transparent",
                  }}
                >
                  <input
                    type="radio"
                    name="corner_radius"
                    value={opt.value}
                    checked={active}
                    onChange={() => set("corner_radius", opt.value)}
                    className="h-4 w-4"
                  />
                  <span>{opt.label}</span>
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>({opt.value})</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Logo URL */}
        <div>
          <label className="label">Logo URL</label>
          <input
            type="url"
            className="input"
            placeholder="https://example.com/logo.png"
            value={form.logo_url}
            onChange={(e) => set("logo_url", e.target.value)}
          />
        </div>

        {/* Cover URL */}
        <div>
          <label className="label">Cover Image URL</label>
          <input
            type="url"
            className="input"
            placeholder="https://example.com/cover.jpg"
            value={form.cover_url}
            onChange={(e) => set("cover_url", e.target.value)}
          />
        </div>
      </section>

      {/* Share + Embed */}
      <section>
        <h2
          style={{
            fontFamily: "'Source Serif 4', Georgia, serif",
            fontWeight: 500,
            fontSize: 22,
            letterSpacing: "-0.012em",
            color: "var(--ink)",
            marginBottom: 6,
          }}
        >
          Share your booking page
        </h2>
        <p style={{ fontSize: 14, color: "var(--ink-soft)", marginBottom: 18, maxWidth: "60ch" }}>
          Send the direct link to customers, or drop the iframe snippet onto your own
          website. The embed adds <code style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12.5, color: "var(--ink)", background: "var(--paper-well)", padding: "1px 6px", borderRadius: 4 }}>?embed=1</code> so the booking page renders without page chrome.
        </p>

        {/* Direct link */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 8,
            background: "var(--paper-well)",
            border: "1px solid var(--rule)",
            borderRadius: 10,
            padding: "8px 8px 8px 14px",
            marginBottom: 12,
          }}
        >
          <span
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 13,
              color: "var(--ink)",
              alignSelf: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {bookingUrl}
          </span>
          <button type="button" className="zd-ghost-btn" onClick={() => copy("link", bookingUrl)}>
            {copied === "link" ? "Copied" : "Copy link"}
          </button>
        </div>

        {/* Embed snippet */}
        <div
          style={{
            background: "var(--paper-well)",
            border: "1px solid var(--rule)",
            borderRadius: 10,
            padding: 14,
          }}
        >
          <pre
            style={{
              margin: 0,
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 12.5,
              lineHeight: 1.55,
              color: "var(--ink)",
              whiteSpace: "pre",
              overflowX: "auto",
            }}
          >{embedSnippet}</pre>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
            <button type="button" className="zd-ghost-btn" onClick={() => copy("embed", embedSnippet)}>
              {copied === "embed" ? "Copied" : "Copy embed code"}
            </button>
          </div>
        </div>
      </section>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <button type="submit" disabled={saving} className="btn-primary">
          {saving ? "Saving..." : "Save Booking Page"}
        </button>
        <button type="button" onClick={openPreview} className="btn-secondary">
          Preview Booking Page
        </button>
      </div>
    </form>
  );
}

// ── Billing Tab ───────────────────────────────────────────────────────────────

interface BillingInfo {
  plan: string;
  status: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

function BillingTab({ slug }: { slug: string }) {
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getBilling(slug)
      .then((data: BillingInfo) => setBilling(data))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { url } = await getBillingPortal(slug) as { url: string };
      window.open(url, "_blank");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) return <div style={{ padding: "32px 0", color: "var(--ink-faint)", fontSize: 14 }}>Loading…</div>;

  return (
    <div className="space-y-10 max-w-2xl">
      {error && <div className="alert-error">{error}</div>}

      <SettingsSection
        title="Subscription"
        lead="Plan, status, and the next billing date. Manage payment methods or cancel through the Stripe portal below."
      >
        {billing && (
          <div
            style={{
              background: "var(--paper-raised)",
              borderRadius: 14,
              padding: 22,
              boxShadow: "var(--shadow-sm)",
            }}
          >
            <BillingRow label="Plan">
              <span style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 18, fontWeight: 500, color: "var(--ink)", textTransform: "capitalize" }}>
                {billing.plan}
              </span>
            </BillingRow>
            <BillingRow label="Status">
              <span className={`badge-${billing.status === "active" ? "green" : billing.status === "trialing" ? "blue" : "red"}`}>
                {billing.status}
              </span>
            </BillingRow>
            {billing.current_period_end && (
              <BillingRow label={billing.cancel_at_period_end ? "Cancels on" : "Renews on"}>
                <span style={{ fontSize: 14, color: "var(--ink)", fontFeatureSettings: "'tnum'" }}>
                  {new Date(billing.current_period_end).toLocaleDateString()}
                </span>
              </BillingRow>
            )}
          </div>
        )}
        {billing?.cancel_at_period_end && (
          <div className="alert-error" style={{ marginTop: 12 }}>
            Your subscription will cancel at the end of the current period.
          </div>
        )}
      </SettingsSection>

      <div>
        <button onClick={openPortal} disabled={portalLoading} className="btn-primary">
          {portalLoading ? "Loading…" : "Manage in Stripe"}
        </button>
        <p style={{ fontSize: 12.5, color: "var(--ink-faint)", marginTop: 10, maxWidth: "60ch" }}>
          You'll be redirected to Stripe's secure billing portal to manage payment methods,
          invoices, or cancel your subscription.
        </p>
      </div>
    </div>
  );
}

function BillingRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 0",
        borderBottom: "1px solid var(--rule)",
      }}
    >
      <span style={{ fontSize: 12.5, color: "var(--ink-soft)", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {label}
      </span>
      {children}
    </div>
  );
}

// ── Main Settings Page ────────────────────────────────────────────────────────

export default function Settings() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const VALID_TABS: Tab[] = [
    "profile",
    "brand",
    "booking_page",
    "scheduling",
    "messaging",
    "billing",
    "payments",
    "integrations",
  ];
  const requestedTab = searchParams.get("tab");
  const initialTab: Tab =
    requestedTab && (VALID_TABS as string[]).includes(requestedTab)
      ? (requestedTab as Tab)
      : "profile";
  const [tab, setTab] = useState<Tab>(initialTab);

  const tabs: { id: Tab; label: string }[] = [
    { id: "profile",      label: "Profile" },
    { id: "brand",        label: "Brand" },
    { id: "booking_page", label: "Booking Page" },
    { id: "scheduling",   label: "Scheduling" },
    { id: "messaging",    label: "Messaging" },
    { id: "billing",      label: "Billing" },
    { id: "payments",     label: "Payments" },
    { id: "integrations", label: "Integrations" },
  ];

  return (
    <div>
      <section className="zd-hero" style={{ marginBottom: 28 }}>
        <div className="zd-hero-text">
          <div className="zd-hero-kicker">
            Configuration <span className="dot">·</span> {tabs.length} sections
          </div>
          <h1 className="zd-hero-headline">Settings</h1>
          <p className="zd-hero-lead">Profile details, brand, scheduling rules, email, and billing — everything that shapes how your business runs in Zedule.</p>
        </div>
      </section>

      {/* Tab bar — ink-underline, design-system tabs */}
      <div style={{ borderBottom: "1px solid var(--rule)", marginBottom: 28 }}>
        <nav className="zd-settings-tabs" style={{ display: "flex", gap: 2, marginBottom: -1, overflowX: "auto", scrollbarWidth: "none" }}>
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={tab === t.id ? "zd-tab active" : "zd-tab"}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {slug && (
        <>
          {tab === "profile"      && <ProfileTab slug={slug} />}
          {tab === "brand"        && <BrandTab slug={slug} />}
          {tab === "booking_page" && <BookingPageTab slug={slug} />}
          {tab === "scheduling"   && <SchedulingTab slug={slug} />}
          {tab === "messaging"    && <MessagingTabWrapper slug={slug} />}
          {tab === "billing"      && <BillingTab slug={slug} />}
          {tab === "payments"     && <PaymentsTab slug={slug} />}
          {tab === "integrations" && <IntegrationsTab slug={slug} />}
        </>
      )}
    </div>
  );
}
